import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import RoleAwareLayout from "../../components/layouts/RoleAwareLayout";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";

const CheckoutPreview = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [error, setError] = useState(null);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [savedCard, setSavedCard] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [confirmError, setConfirmError] = useState(null);

  const fetchSubscriptionStatus = useCallback(async () => {
    const token = localStorage.getItem("ogestyle_token");
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/subscription-new/status`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setSubscription(response.data);

      // Redirect if trial is still active
      if (response.data.isInTrial) {
        const paymentPath =
          response.data?.entityType === "freelancer"
            ? "/dashboard/freelancer/subscription/payment"
            : "/dashboard/settings/subscription/payment";
        navigate(paymentPath);
        return;
      }
    } catch (err) {
      setError("Failed to load subscription details");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchSubscriptionStatus();
  }, [fetchSubscriptionStatus]);

  const handlePayNow = async () => {
    const token = localStorage.getItem("ogestyle_token");
    setProcessing(true);
    setError(null);

    try {
      // Check if user has a payment method on file
      const pmResponse = await axios.get(
        `${API_BASE_URL}/api/subscription-new/payment-methods`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const methods = pmResponse.data.paymentMethods || [];
      const hasCard = methods.length > 0;

      if (!hasCard) {
        // No card on file — redirect to billing page to add one first
        const billingPath =
          subscription?.entityType === "freelancer"
            ? "/dashboard/freelancer/billing"
            : "/dashboard/settings/invoices";
        navigate(billingPath);
        return;
      }

      // Has card — show confirmation modal (use default card, or first card)
      const defaultCard =
        methods.find((m) => m.isDefault) || methods[0];
      setSavedCard(defaultCard);
      setShowModal(true);
    } catch (err) {
      setError("Failed to load payment details. Please try again.");
    } finally {
      setProcessing(false);
    }
  };

  const handleConfirmDirect = async () => {
    const token = localStorage.getItem("ogestyle_token");
    setConfirmLoading(true);
    setConfirmError(null);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/subscription-new/create-subscription-direct`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data.success) {
        const dashboardPath =
          subscription?.entityType === "freelancer"
            ? "/dashboard/freelancer"
            : "/dashboard";
        navigate(`${dashboardPath}?subscribed=true`);
      } else {
        setConfirmError("Something went wrong. Please try again.");
        setConfirmLoading(false);
      }
    } catch (err) {
      setConfirmError(
        err.response?.data?.message ||
          "Payment failed. Please try again.",
      );
      setConfirmLoading(false);
    }
  };

  const billingPath =
    subscription?.entityType === "freelancer"
      ? "/dashboard/freelancer/billing"
      : "/dashboard/settings/invoices";

  if (loading) {
    return (
      <RoleAwareLayout>
        <div className="settings-page" style={{ padding: "40px", textAlign: "center" }}>
          <div
            style={{
              width: "40px",
              height: "40px",
              border: "4px solid #f3f4f6",
              borderTop: "4px solid #3b82f6",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              margin: "0 auto 20px",
            }}
          />
          <p style={{ color: "#6b7280" }}>Loading subscription details...</p>
        </div>
      </RoleAwareLayout>
    );
  }

  const entityType = subscription?.entityType || "business";
  const isFreelancer = entityType === "freelancer";

  // Use locationCount directly from API response
  const locationCount = subscription?.locationCount || 1;
  const pricing = subscription?.pricing || {};
  const boothRenterCount = subscription?.boothRenterCount || 0;

  // Calculate pricing based on entity type
  let basePrice, additionalPrice, boothRenterPrice, totalMonthly;

  if (isFreelancer) {
    basePrice = pricing.freelancerMonthly || 40;
    additionalPrice = 0;
    boothRenterPrice = 0;
    totalMonthly = basePrice;
  } else {
    basePrice = pricing.businessMonthly || 65;
    additionalPrice = pricing.businessAdditional || 45;
    boothRenterPrice = pricing.boothRenterMonthly || 0;
    totalMonthly =
      basePrice +
      (locationCount - 1) * additionalPrice +
      boothRenterCount * boothRenterPrice;
  }

  return (
    <RoleAwareLayout>
      <div
        className="settings-page"
        style={{ maxWidth: "700px", margin: "0 auto", padding: "24px" }}
      >
        {/* Header */}
        <div style={{ marginBottom: "32px", textAlign: "center" }}>
          <div style={{ fontSize: "48px", marginBottom: "16px" }}>💳</div>
          <h1
            style={{
              fontSize: "32px",
              fontWeight: "700",
              margin: "0 0 12px",
              color: "#111827",
            }}
          >
            OGESTYLE
          </h1>
          <p
            style={{
              fontSize: "16px",
              color: "#6b7280",
              margin: 0,
            }}
          >
            Complete your subscription to restore full access
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div
            style={{
              background: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: "8px",
              padding: "14px 16px",
              marginBottom: "24px",
              color: "#dc2626",
              fontSize: "14px",
            }}
          >
            ⚠️ {error}
          </div>
        )}

        {/* Pricing Breakdown */}
        <div
          style={{
            background: "white",
            border: "2px solid #e5e7eb",
            borderRadius: "12px",
            padding: "24px",
            marginBottom: "24px",
          }}
        >
          <h2
            style={{
              fontSize: "18px",
              fontWeight: "600",
              margin: "0 0 20px",
              color: "#111827",
            }}
          >
            Subscription Summary
          </h2>

          {isFreelancer ? (
            // Freelancer Pricing - Simple flat rate
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "12px 0",
                borderBottom: "1px solid #f3f4f6",
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: "15px",
                    fontWeight: "500",
                    color: "#374151",
                  }}
                >
                  Freelancer Subscription
                </div>
                <div
                  style={{
                    fontSize: "13px",
                    color: "#6b7280",
                    marginTop: "2px",
                  }}
                >
                  Full platform access
                </div>
              </div>
              <div
                style={{
                  fontSize: "16px",
                  fontWeight: "600",
                  color: "#111827",
                }}
              >
                ${basePrice.toFixed(2)}/mo
              </div>
            </div>
          ) : (
            // Business Pricing - Base + Additional locations
            <>
              {/* Base Price */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "12px 0",
                  borderBottom: "1px solid #f3f4f6",
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: "15px",
                      fontWeight: "500",
                      color: "#374151",
                    }}
                  >
                    Base Subscription
                  </div>
                  <div
                    style={{
                      fontSize: "13px",
                      color: "#6b7280",
                      marginTop: "2px",
                    }}
                  >
                    First location included
                  </div>
                </div>
                <div
                  style={{
                    fontSize: "16px",
                    fontWeight: "600",
                    color: "#111827",
                  }}
                >
                  ${basePrice.toFixed(2)}/mo
                </div>
              </div>

              {/* Additional Locations */}
              {locationCount > 1 && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "12px 0",
                    borderBottom: "1px solid #f3f4f6",
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: "15px",
                        fontWeight: "500",
                        color: "#374151",
                      }}
                    >
                      Additional Locations
                    </div>
                    <div
                      style={{
                        fontSize: "13px",
                        color: "#6b7280",
                        marginTop: "2px",
                      }}
                    >
                      {locationCount - 1} location
                      {locationCount - 1 > 1 ? "s" : ""} × $
                      {additionalPrice.toFixed(2)}/mo
                    </div>
                  </div>
                  <div
                    style={{
                      fontSize: "16px",
                      fontWeight: "600",
                      color: "#111827",
                    }}
                  >
                    ${((locationCount - 1) * additionalPrice).toFixed(2)}/mo
                  </div>
                </div>
              )}

              {/* Active Booth Renters */}
              {boothRenterCount > 0 && boothRenterPrice > 0 && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "12px 0",
                    borderBottom: "1px solid #f3f4f6",
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: "15px",
                        fontWeight: "500",
                        color: "#374151",
                      }}
                    >
                      Active Booth Renters
                    </div>
                    <div
                      style={{
                        fontSize: "13px",
                        color: "#6b7280",
                        marginTop: "2px",
                      }}
                    >
                      {boothRenterCount} renter
                      {boothRenterCount > 1 ? "s" : ""} × ${boothRenterPrice.toFixed(2)}/mo
                    </div>
                  </div>
                  <div
                    style={{
                      fontSize: "16px",
                      fontWeight: "600",
                      color: "#111827",
                    }}
                  >
                    ${(boothRenterCount * boothRenterPrice).toFixed(2)}/mo
                  </div>
                </div>
              )}
            </>
          )}

          {/* Total */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "16px 0 0",
              marginTop: "8px",
            }}
          >
            <div>
              <div
                style={{
                  fontSize: "18px",
                  fontWeight: "700",
                  color: "#111827",
                }}
              >
                Total Monthly
              </div>
              <div
                style={{ fontSize: "13px", color: "#6b7280", marginTop: "2px" }}
              >
                {isFreelancer
                  ? "Billed monthly, cancel anytime"
                  : `${locationCount} location${locationCount > 1 ? "s" : ""}${boothRenterCount > 0 ? ` • ${boothRenterCount} booth renter${boothRenterCount > 1 ? "s" : ""}` : ""} • Billed monthly`}
              </div>
            </div>
            <div
              style={{ fontSize: "28px", fontWeight: "700", color: "#2563eb" }}
            >
              ${totalMonthly.toFixed(2)}/mo
            </div>
          </div>
        </div>

        {/* What You Get */}
        <div
          style={{
            background: "#f0f9ff",
            border: "1px solid #bfdbfe",
            borderRadius: "12px",
            padding: "20px",
            marginBottom: "24px",
          }}
        >
          <h3
            style={{
              fontSize: "16px",
              fontWeight: "600",
              margin: "0 0 12px",
              color: "#1e40af",
            }}
          >
            ✨ What's Included
          </h3>
          <ul
            style={{
              fontSize: "14px",
              color: "#1e40af",
              margin: 0,
              paddingLeft: "20px",
              lineHeight: "1.8",
            }}
          >
            <li>Unlimited bookings and appointments</li>
            <li>Customer management and analytics</li>
            <li>Stripe payment processing</li>
            <li>Marketing tools and email campaigns</li>
            <li>Mobile-friendly booking pages</li>
            <li>24/7 customer support</li>
          </ul>
        </div>

        {/* Pay Now Button */}
        <button
          onClick={handlePayNow}
          disabled={processing}
          style={{
            width: "100%",
            background: processing
              ? "#9ca3af"
              : "linear-gradient(135deg, #f59e0b 0%, #f97316 100%)",
            color: "white",
            border: "none",
            padding: "18px",
            borderRadius: "12px",
            fontSize: "18px",
            fontWeight: "700",
            cursor: processing ? "not-allowed" : "pointer",
            boxShadow: processing
              ? "none"
              : "0 4px 12px rgba(245, 158, 11, 0.3)",
            transition: "all 0.2s",
          }}
          onMouseEnter={(e) => {
            if (!processing) {
              e.target.style.transform = "translateY(-2px)";
              e.target.style.boxShadow = "0 6px 16px rgba(245, 158, 11, 0.4)";
            }
          }}
          onMouseLeave={(e) => {
            if (!processing) {
              e.target.style.transform = "translateY(0)";
              e.target.style.boxShadow = "0 4px 12px rgba(245, 158, 11, 0.3)";
            }
          }}
        >
          {processing ? "⏳ Redirecting to payment..." : "💳 Pay Now"}
        </button>

        {/* Trust Badges */}
        <div
          style={{
            marginTop: "24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "24px",
            fontSize: "12px",
            color: "#9ca3af",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <span>🔒</span>
            <span>Secured by Stripe</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <span>✅</span>
            <span>PCI Compliant</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <span>💎</span>
            <span>Cancel Anytime</span>
          </div>
        </div>
      </div>

      {/* Confirm & Pay Modal */}
      {showModal && savedCard && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "16px",
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget && !confirmLoading) {
              setShowModal(false);
              setConfirmError(null);
            }
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: "16px",
              padding: "32px",
              maxWidth: "440px",
              width: "100%",
              boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
            }}
          >
            <h2
              style={{
                fontSize: "20px",
                fontWeight: "700",
                margin: "0 0 8px",
                color: "#111827",
              }}
            >
              Confirm Subscription
            </h2>
            <p
              style={{
                fontSize: "14px",
                color: "#6b7280",
                margin: "0 0 24px",
              }}
            >
              Review your plan and confirm payment below.
            </p>

            {/* Plan summary */}
            <div
              style={{
                background: "#f9fafb",
                border: "1px solid #e5e7eb",
                borderRadius: "10px",
                padding: "16px",
                marginBottom: "16px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: "15px",
                      fontWeight: "600",
                      color: "#111827",
                    }}
                  >
                    {isFreelancer
                      ? "Freelancer Monthly"
                      : `Business Monthly${locationCount > 1 ? ` (${locationCount} locations)` : ""}`}
                  </div>
                  <div
                    style={{
                      fontSize: "13px",
                      color: "#6b7280",
                      marginTop: "2px",
                    }}
                  >
                    Billed monthly · Cancel anytime
                  </div>
                </div>
                <div
                  style={{
                    fontSize: "20px",
                    fontWeight: "700",
                    color: "#2563eb",
                  }}
                >
                  ${totalMonthly.toFixed(2)}/mo
                </div>
              </div>
            </div>

            {/* Saved card */}
            <div
              style={{
                background: "#f9fafb",
                border: "1px solid #e5e7eb",
                borderRadius: "10px",
                padding: "16px",
                marginBottom: "24px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <span style={{ fontSize: "24px" }}>💳</span>
                <div>
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#111827",
                      textTransform: "capitalize",
                    }}
                  >
                    {savedCard.brand} ending in {savedCard.last4}
                  </div>
                  <div style={{ fontSize: "12px", color: "#6b7280" }}>
                    Expires {savedCard.expMonth}/{savedCard.expYear}
                  </div>
                </div>
              </div>
              {savedCard.isDefault && (
                <span
                  style={{
                    fontSize: "11px",
                    background: "#dbeafe",
                    color: "#1d4ed8",
                    padding: "2px 8px",
                    borderRadius: "4px",
                    fontWeight: "500",
                  }}
                >
                  Default
                </span>
              )}
            </div>

            {/* Error */}
            {confirmError && (
              <div
                style={{
                  background: "#fef2f2",
                  border: "1px solid #fecaca",
                  borderRadius: "8px",
                  padding: "12px 14px",
                  marginBottom: "16px",
                  fontSize: "13px",
                  color: "#dc2626",
                }}
              >
                ⚠️ {confirmError}
                <button
                  onClick={() => navigate(billingPath)}
                  style={{
                    display: "block",
                    marginTop: "8px",
                    background: "none",
                    border: "none",
                    color: "#2563eb",
                    fontSize: "13px",
                    cursor: "pointer",
                    padding: 0,
                    textDecoration: "underline",
                  }}
                >
                  Update payment method →
                </button>
              </div>
            )}

            {/* Buttons */}
            <div style={{ display: "flex", gap: "12px" }}>
              <button
                onClick={() => {
                  setShowModal(false);
                  setConfirmError(null);
                }}
                disabled={confirmLoading}
                style={{
                  flex: 1,
                  padding: "13px",
                  background: "white",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#374151",
                  cursor: confirmLoading ? "not-allowed" : "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDirect}
                disabled={confirmLoading}
                style={{
                  flex: 2,
                  padding: "13px",
                  background: confirmLoading
                    ? "#9ca3af"
                    : "linear-gradient(135deg, #f59e0b 0%, #f97316 100%)",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "14px",
                  fontWeight: "700",
                  cursor: confirmLoading ? "not-allowed" : "pointer",
                  boxShadow: confirmLoading
                    ? "none"
                    : "0 4px 12px rgba(245, 158, 11, 0.3)",
                }}
              >
                {confirmLoading ? "⏳ Processing..." : "✓ Confirm & Pay"}
              </button>
            </div>
          </div>
        </div>
      )}
    </RoleAwareLayout>
  );
};

export default CheckoutPreview;
